import type { NavbarProps } from './NavbarProps.d';

import React, { useState, useEffect } from 'react';
import { faBars, faBell, faXmark, faExternalLink } from '@fortawesome/pro-light-svg-icons';
import { DropdownMenuItem } from '@hdir/ui/dropdown-menu';
import { Link } from '@hdir/ui/link';

import { Button } from '../../components/Button';
import { DropdownMenu } from '../../components/DropdownMenu';
import { Div } from '/react4xp/components/Element';
import { FontAwesomeIconWrapper } from '../../components/FontAwesomeIconWrapper';
import { ODF_LOGO_ALT_TEXT } from '/react4xp/overdosefare/constants';
import { SkipToMainContent } from "@dip/helsecore/src/components/shared/SkipToMainContent/SkipToMainContent";
// import { SearchBar } from "/react4xp/shared/SearchBar/SearchBar";
// import { clsx } from "clsx";

const SUBSCRIBE_URL = 'https://abonnering.overdosefare.no';
// const UNSUBSCRIBE_URL = 'https://abonnering.overdosefare.no/avmelding';

// function getFocusableElements(container: HTMLElement): NodeListOf<HTMLElement> {
// 	return container.querySelectorAll<HTMLElement>('input, button');
// }

export function Navbar({ navbarData, currentPath }: NavbarProps): React.JSX.Element | null {
	if (!navbarData) return null;

	const {
		logo,
		baseUrl,
		linkRow,
		dropdown,
		// searchResultDestination,
	} = navbarData;

	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const menuRef = React.useRef<HTMLInputElement>(null);
	// const mobileSearchButtonRef = useRef<HTMLButtonElement>(null);
	// const [searchMenuOpen, setSearchMenuOpen] = useState(false);
	// const searchMenuRef = useRef<HTMLDivElement>(null);
	// const isOverlayVisible = isMenuOpen || searchMenuOpen;

	// Handle mobile menu interactions
	useEffect(() => {
		const handleResize = (): void => {
			if (window.innerWidth >= 768) {
				setIsMenuOpen(false);
			}
		};

		const handleOpenMenuLoop = (e: KeyboardEvent): void => {
			if (e.key === 'Tab') {
				const focusableElements = menuRef.current?.querySelectorAll('a, button');
				if (!focusableElements) return;

				const firstElement = focusableElements[0] as HTMLElement;
				const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

				if (e.shiftKey) {
					if (document.activeElement === firstElement) {
						e.preventDefault();
						lastElement.focus();
					}
				} else {
					if (document.activeElement === lastElement) {
						e.preventDefault();
						firstElement.focus();
					}
				}
			}

			// Close on escape
			if (e.key === "Escape") {
				setIsMenuOpen(false);
				document.removeEventListener('keydown', handleOpenMenuLoop);
				window.removeEventListener('resize', handleResize);
			}
		};

		// Add or remove listeners
		if (isMenuOpen) {
			document.addEventListener('keydown', handleOpenMenuLoop);
			window.addEventListener('resize', handleResize);
		} else {
			document.removeEventListener('keydown', handleOpenMenuLoop);
			window.removeEventListener('resize', handleResize);
		}
	}, [isMenuOpen]);

	// Toggle menu
	const toggleMenu = (): void => {
		setIsMenuOpen((prevState) => !prevState);
	};

	// Handle keydown for Enter and Space
	const handleEnterOrSpace = (e: React.KeyboardEvent<HTMLButtonElement>): void => {
		if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
			e.preventDefault();
			e.stopPropagation();
			toggleMenu();
		}
	};

	// TODO: Keep search functionality for future purposes
	// const toggleSearchMenu = (): void => {
	// 	setSearchMenuOpen(prev => {
	// 		const next = !prev;
	// 		if (next) setIsMenuOpen(false);
	// 		return next;
	// 	});
	// };

	// // Handle SearchBar submit
	// const handleSearchSubmit = (q: string): void => {
	// 	const encodedQuery = encodeURIComponent(q);
	// 	window.location.href = `${searchResultDestination}?q=${encodedQuery}`;
	// };

	// useEffect(() => {
	// 	if (!searchMenuOpen) return;
	//
	// 	const menuEl = searchMenuRef.current;
	// 	if (!menuEl) return;
	//
	// 	const input = menuEl.querySelector("input") as HTMLInputElement | null;
	// 	input?.focus();
	//
	// 	const focusable = getFocusableElements(menuEl);
	// 	const first = focusable[0];
	// 	const last = focusable[focusable.length - 1];
	//
	// 	function closeMenu(): void {
	// 		// Move focus out before closing
	// 		mobileSearchButtonRef.current?.focus();
	// 		setSearchMenuOpen(false);
	// 	}
	//
	// 	function handleKey(e: KeyboardEvent): void {
	// 		// Close search menu on escape
	// 		if (e.key === "Escape") {
	// 			e.preventDefault();
	// 			closeMenu();
	// 		}
	//
	// 		if (e.key !== "Tab") return;
	//
	// 		// Leave at tab out
	// 		if (e.shiftKey && document.activeElement === first) {
	// 			e.preventDefault();
	// 			closeMenu();
	// 		}
	//
	// 		if (!e.shiftKey && document.activeElement === last) {
	// 			e.preventDefault();
	// 			closeMenu();
	// 		}
	// 	}
	//
	// 	// Add listener on opening
	// 	document.addEventListener("keydown", handleKey);
	//
	// 	return () => {
	// 		document.removeEventListener("keydown", handleKey);
	// 	};
	// }, [searchMenuOpen]);

	// const searchMenuClasses = clsx(
	// 	"absolute z-30 top-full left-0 p-0 py-lg w-full bg-surface-neutral-default",
	// 	"transition-transform transition-opacity duration-150 ease-out origin-top",
	// 	searchMenuOpen
	// 		? "opacity-100 scale-y-100"
	// 		: "opacity-0 scale-y-95 pointer-events-none",
	// );

	return (
		<div className="relative">
			<SkipToMainContent selector='main[data-portal-region="main"]' />
			<nav className="relative bg-surface-action" ref={menuRef}>
				<div className="py-lg tablet:py-xl mx-auto w-[95vw] shadow-navbar">
					<div className="flex justify-between items-center max-w-content mx-auto w-full">
						<Link href={baseUrl}>
							<img
								alt={ODF_LOGO_ALT_TEXT}
								className="h-12 tablet:h-14 w-auto"
								src={logo}
							/>
						</Link>

						<div className="flex items-center gap-md">
							{/* /!* Search menu toggler *!/ */}
							{/* <div className="tablet:hidden h-fit w-fit"> */}
							{/*	<Button */}
							{/*		variant="icon" */}
							{/*		ref={mobileSearchButtonRef} */}
							{/*		onClick={toggleSearchMenu} */}
							{/*		aria-expanded={searchMenuOpen} */}
							{/*		aria-controls="mobile-search-menu" */}
							{/*		aria-label={!searchMenuOpen ? "Åpne søkemeny" : "Lukk søkemeny"} */}
							{/*	> */}
							{/*		<FontAwesomeIconWrapper fixedWidth={true} icon={faSearch} size="lg"/> */}
							{/*	</Button> */}
							{/* </div> */}
							<Button
								aria-haspopup="dialog"
								aria-controls="mobile-menu"
								aria-expanded={isMenuOpen}
								variant="secondary"
								onClick={toggleMenu}
								onKeyDown={handleEnterOrSpace}
								className="tablet:hidden"
							>
								<span className="flex items-center gap-xs text-xl">
									<FontAwesomeIconWrapper fixedWidth={true} icon={!isMenuOpen ? faBars : faXmark} size="lg" />
									Meny
								</span>
							</Button>
						</div>

						<div
							className="hidden gap-md tablet:flex" // Necessary evil as Dropdowns are wrapped in an untargetable empty div
						>
							<Link
								href={SUBSCRIBE_URL}
								size='medium'
								variant='button-secondary'
							>
								<FontAwesomeIconWrapper fixedWidth={true} icon={faBell} />
								Få varsler på sms/epost
							</Link>
							<DropdownMenu
								id="menu-dropdown"
								label={dropdown?.dropdownText}
								size='medium'
								variant="primary"
							>
								{
									dropdown?.links.map((item, index) => (
										<DropdownMenuItem as="a" href={item.link?.href} key={index}>{item.linkText}</DropdownMenuItem>
									))
								}
							</DropdownMenu>
						</div>
					</div>
				</div>

				<div className="hidden py-lg tablet:py-xl px-md tablet:px-lg desktop:px-xl tablet:flex grow w-full border-x-[0px] border-y-[1px] border-solid border-[#D2D5D8] shadow-navbar">
					<div className="flex mx-auto w-full max-w-content gap-xxl justify-between">
						<div className="flex mx-auto w-full max-w-content gap-xxl min-w-0">
							{linkRow?.map((link, index) => (
								<Link
									key={index}
									href={link.link?.href}
									target={link.link?.isExternalLink ? '_blank' : ''}
									className={`${!link.link?.isExternalLink && currentPath === link.link?.href ? 'active-link !text-[var(--design-color-text-action-default)] underline' : '!text-neutral !no-underline'} text-xl !gap-md !transform-none font-semibold hover:!underline underline-offset-4`}
								>
									{link.linkText}
									{link.link?.isExternalLink && (<>
										<span className='sr-only'>, {link.link?.hostname}</span>
										<FontAwesomeIconWrapper
											className="!fill-action !text-xl"
											fixedWidth={true}
											icon={faExternalLink}
										/>
									</>)}
								</Link>
							))}
						</div>
						{/* <SearchBar placeholder="Søk etter" onSubmit={handleSearchSubmit}/> */}
					</div>
				</div>

				{isMenuOpen && ( // Mobile menu dialog
					<dialog id="mobile-menu" className="absolute tablet:hide w-full !h-[calc(100vh-96px)] !border-none !p-0 bg-[#00000040] z-50" aria-modal={isMenuOpen} open={isMenuOpen}>
						<nav className="flex flex-col h-full !bg-transparent">
							{linkRow?.map((link, index) => (
								<div key={index} className="p-md border-[0px] border-t-[1px] border-solid border-[#D2D5D8] bg-surface-action-secondary text-action flex">
									<Link
										href={link.link?.href}
										target={link.link?.isExternalLink ? '_blank' : ''}
										className="text-xl !gap-md !transform-none font-semibold !text-neutral !no-underline hover:!underline"
									>
										{link.linkText}
										{link.link?.isExternalLink && (<>
											<span className='sr-only'>, {link.link?.hostname}</span>
											<FontAwesomeIconWrapper
												className="!fill-action !text-xl"
												fixedWidth={true}
												icon={faExternalLink}
											/>
										</>)}
									</Link>
								</div>
							))}
							<div className="flex flex-col gap-md px-md py-xxl border-[0px] border-t-[1px] border-solid border-[#D2D5D8] bg-surface-action-secondary">
								<p className="text-xl font-semibold">
									{dropdown?.dropdownText}:
								</p>
								{dropdown?.links.map((link, index) => (
									<Link
										href={link.link?.href}
										target={link.link?.isExternalLink ? '_blank' : ''}
										key={index}
										size="large"
										className="!w-full justify-center"
										variant={index > 0 ? 'button-secondary' : 'button-primary'}
									>
										<span className="break-words whitespace-normal">{link.linkText}</span>
										{
											link.link?.isExternalLink
												? <span className='sr-only'>, {link.link?.hostname}</span>
												: null
										}
									</Link>
								))}
							</div>
							<Div
								className={[
									'bg-surface-action-secondary',
									'border-[#D2D5D8]',
									'border-[0px]',
									'border-solid',
									'border-t-[1px]',
									'flex',
									'items-stretch',
									'p-md',
									'text-action',
								]}
							>
								<Link
									className='justify-center !w-full'
									href={SUBSCRIBE_URL}
									size='large'
									variant='button-secondary'
								>
									<FontAwesomeIconWrapper fixedWidth={true} icon={faBell}/>
									Få varsler på sms/epost
								</Link>
							</Div>
							<div className="flex grow h-full" onClick={toggleMenu} />
						</nav>
					</dialog>
				)}
			</nav>

			{/* <div */}
			{/*	ref={searchMenuRef} */}
			{/*	id="mobile-search-menu" */}
			{/*	role="search" */}
			{/*	aria-label="Søk" */}
			{/*	aria-hidden={!searchMenuOpen} */}
			{/*	className={searchMenuClasses} */}
			{/* > */}
			{/*	<div className="mx-auto w-[95vw]"> */}
			{/*		<SearchBar placeholder="Søk etter" fullWidth={true} onSubmit={handleSearchSubmit}/> */}
			{/*	</div> */}
			{/* </div> */}
		</div>
	);
}
